import { useCallback, useEffect, useRef, useState } from "react";
import { crDate } from "@/lib/dateFormat";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ResourcePicker } from "@/components/ResourcePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, rectSortingStrategy, useSortable, arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  FileText, Table2, Upload, BookOpen, ExternalLink, Trash2, Pencil, FolderOpen, Link2, Loader2,
  LayoutGrid, List, GripVertical,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ITEM_ICON = { doc: FileText, sheet: Table2, file: Upload, link: Link2 };

/** Envoltura para arrastrar un archivo y acomodarlo en el orden que la clase necesita. */
const Sortable = ({ id, disabled, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled });
  return children({
    ref: setNodeRef,
    style: {
      transform: CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 20 : undefined,
      position: isDragging ? "relative" : undefined,
    },
    isDragging,
    handle: { ...attributes, ...listeners },
  });
};

/** Google preview URL: read-only view that can be embedded inside the app. */
const previewSrc = (it) => {
  if (it.kind === "link") return it.web_link;
  if (it.drive_file_id) {
    if (it.kind === "doc") return `https://docs.google.com/document/d/${it.drive_file_id}/preview`;
    if (it.kind === "sheet") return `https://docs.google.com/spreadsheets/d/${it.drive_file_id}/preview`;
    return `https://drive.google.com/file/d/${it.drive_file_id}/preview`;
  }
  return it.web_link;
};

/** Drive-backed notebook for one class. */
export const NotebookPanel = ({ studentId, groupId, canEdit = true }) => {
  const { user } = useAuth();
  const [view, setView] = useState(() => localStorage.getItem("notebook_view") || "cards");
  useEffect(() => { localStorage.setItem("notebook_view", view); }, [view]);
  const isStudent = user?.role === "student";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({ title: "", kind: "doc", template_id: "" });
  const [templates, setTemplates] = useState([]);
  const [renaming, setRenaming] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const fileRef = useRef(null);
  const params = studentId ? { student_id: studentId } : groupId ? { group_id: groupId } : {};

  const openItem = (it) => {
    if (it.kind === "link") window.open(it.web_link, "_blank", "noopener,noreferrer");
    else setViewing(it);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/notebook", { params });
      setData(r.data);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || "No se pudo cargar el notebook" });
    } finally {
      setLoading(false);
    }
    /* eslint-disable-next-line */
  }, [studentId, groupId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isStudent) api.get("/notebook/templates").then((r) => setTemplates(r.data || [])).catch(() => {});
  }, [isStudent]);

  // The template is pre-selected (last one used, or the only one) so nobody ends up with a blank doc.
  const defaultTemplate = (kind) => {
    const mine = templates.filter((tp) => tp.kind === kind);
    if (!mine.length) return "";
    const last = localStorage.getItem(`notebook_tpl_${kind}`);
    return mine.some((tp) => tp.id === last) ? last : mine[0].id;
  };

  const createDoc = async () => {
    if (!newForm.title.trim()) { toast.error("Poné un nombre"); return; }
    setBusy(true);
    try {
      const r = await api.post("/notebook/doc", { ...newForm, ...params });
      if (newForm.template_id) localStorage.setItem(`notebook_tpl_${newForm.kind}`, newForm.template_id);
      setNewOpen(false);
      setNewForm({ title: "", kind: "doc", template_id: "" });
      window.open(r.data.web_link, "_blank", "noopener,noreferrer");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear");
    } finally { setBusy(false); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post("/notebook/upload", fd, { params });
      toast.success("Archivo subido");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addFromResources = async (ids) => {
    setPickerOpen(false);
    if (!ids?.length) return;
    setBusy(true);
    try {
      for (const rid of ids) {
        await api.post("/notebook/from-resource", { resource_id: rid, ...params });
      }
      toast.success("Material archivado en el notebook");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo archivar");
    } finally { setBusy(false); }
  };

  const rename = async () => {
    if (!renaming?.name.trim()) return;
    try {
      await api.patch(`/notebook/item/${renaming.id}`, { name: renaming.name });
      setRenaming(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo renombrar"); }
  };

  const remove = async (item) => {
    if (!window.confirm(`¿Quitar "${item.name}" del notebook?`)) return;
    try {
      await api.delete(`/notebook/item/${item.id}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo borrar"); }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Se acomoda en pantalla al instante y se guarda atrás; si falla, se devuelve.
  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = data.items.map((i) => i.id);
    const from = ids.indexOf(active.id);
    const to = ids.indexOf(over.id);
    if (from < 0 || to < 0) return;
    const previos = data.items;
    const nuevos = arrayMove(previos, from, to);
    setData((d) => ({ ...d, items: nuevos }));
    try {
      await api.patch("/notebook/reorder", { ids: nuevos.map((i) => i.id), ...params });
    } catch (e) {
      setData((d) => ({ ...d, items: previos }));
      toast.error(e?.response?.data?.detail || "No se pudo guardar el orden");
    }
  };

  if (loading) {
    return <Card className="p-10 text-center text-slate-500" data-testid="notebook-loading">
      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
    </Card>;
  }

  if (data?.error) {
    return <Card className="p-8 text-center" data-testid="notebook-error">
      <p className="text-sm text-slate-600">{data.error}</p>
    </Card>;
  }

  return (
    <div data-testid="notebook-panel">
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {!isStudent && (
            <>
              <Button onClick={() => { setNewForm({ title: "", kind: "doc", template_id: defaultTemplate("doc") }); setNewOpen(true); }} disabled={busy} data-testid="notebook-new-doc">
                <FileText className="h-4 w-4" /> Nuevo Doc
              </Button>
              <Button variant="outline" onClick={() => { setNewForm({ title: "", kind: "sheet", template_id: defaultTemplate("sheet") }); setNewOpen(true); }} disabled={busy} data-testid="notebook-new-sheet">
                <Table2 className="h-4 w-4" /> Nueva Hoja
              </Button>
              <Button variant="outline" onClick={() => setPickerOpen(true)} disabled={busy} data-testid="notebook-from-resources">
                <BookOpen className="h-4 w-4" /> Traer Recurso
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="notebook-upload">
            <Upload className="h-4 w-4" /> Subir Archivo
          </Button>
          {data?.folder_link && (
            <Button variant="ghost" asChild data-testid="notebook-open-folder">
              <a href={data.folder_link} target="_blank" rel="noopener noreferrer">
                <FolderOpen className="h-4 w-4" /> Abrir en Drive
              </a>
            </Button>
          )}
          <input ref={fileRef} type="file" className="hidden" onChange={upload} data-testid="notebook-file-input" />
          <div className="ml-auto flex items-center rounded-lg border border-slate-200 overflow-hidden">
            <button type="button" onClick={() => setView("cards")} data-testid="notebook-view-cards"
                    title="Ver en tarjetas"
                    className={`h-9 w-9 grid place-items-center ${view === "cards" ? "bg-slate-100 text-slate-900" : "text-slate-400 hover:text-slate-600"}`}>
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => setView("list")} data-testid="notebook-view-list"
                    title="Ver en renglones"
                    className={`h-9 w-9 grid place-items-center border-l border-slate-200 ${view === "list" ? "bg-slate-100 text-slate-900" : "text-slate-400 hover:text-slate-600"}`}>
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {canEdit && (data?.items || []).length > 1 && (
        <p className="text-[11px] text-slate-400 mb-2 flex items-center gap-1" data-testid="notebook-reorder-hint">
          <GripVertical className="h-3 w-3" /> Arrastrá los archivos para acomodarlos en el orden que querés
        </p>
      )}

      {(data?.items || []).length === 0 ? (
        <Card className="p-10 text-center text-slate-500" data-testid="notebook-empty">
          <FolderOpen className="h-8 w-8 mx-auto mb-3 text-slate-300" />
          Todavía no hay nada en este notebook
        </Card>
      ) : view === "list" ? (
        <Card className="overflow-hidden" data-testid="notebook-items-list">
          <div className="hidden sm:flex items-center gap-3 px-4 py-2 border-b border-slate-100 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
            {canEdit && <span className="w-4" />}
            <span className="flex-1">Nombre</span>
            <span className="w-40">Creado por</span>
            <span className="w-28">Fecha</span>
            <span className="w-24 text-right">Acciones</span>
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={data.items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              {data.items.map((it) => {
                const Icon = ITEM_ICON[it.kind] || FileText;
                return (
                  <Sortable key={it.id} id={it.id} disabled={!canEdit}>
                    {({ ref, style, isDragging, handle }) => (
                      <div ref={ref} style={style}
                           className={`relative flex items-center gap-3 px-4 py-2.5 border-b border-slate-50 last:border-0 ${isDragging ? "bg-white shadow-lg rounded-lg" : "hover:bg-slate-50"}`}
                           data-testid={`notebook-row-${it.id}`}>
                        {canEdit && (
                          <button type="button" {...handle} title="Arrastrar para acomodar"
                                  className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 touch-none"
                                  data-testid={`notebook-drag-${it.id}`}>
                            <GripVertical className="h-4 w-4" />
                          </button>
                        )}
                        <Icon className="h-4 w-4 text-slate-500 shrink-0" />
                        <button type="button" onClick={() => openItem(it)} data-testid={`notebook-open-${it.id}`}
                                className="flex-1 min-w-0 text-left text-sm text-slate-800 hover:text-brand truncate">
                          {it.name}
                        </button>
                        <span className="hidden sm:block w-40 text-xs text-slate-500 truncate">{it.created_by_name || "—"}</span>
                        <span className="hidden sm:block w-28 text-xs text-slate-500">
                          {it.created_at ? crDate(it.created_at) : "—"}
                        </span>
                        <span className="w-24 flex items-center justify-end gap-0.5">
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openItem(it)} data-testid={`notebook-cta-${it.id}`}>
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                          {canEdit && (
                            <>
                              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setRenaming({ id: it.id, name: it.name })} data-testid={`notebook-rename-${it.id}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(it)} data-testid={`notebook-delete-${it.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                        </span>
                      </div>
                    )}
                  </Sortable>
                );
              })}
            </SortableContext>
          </DndContext>
        </Card>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={data.items.map((i) => i.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="notebook-items">
              {data.items.map((it) => {
                const Icon = ITEM_ICON[it.kind] || FileText;
                return (
                  <Sortable key={it.id} id={it.id} disabled={!canEdit}>
                    {({ ref, style, isDragging, handle }) => (
                      <Card ref={ref} style={style}
                            className={`p-4 flex items-start gap-3 transition-shadow ${isDragging ? "shadow-xl" : "hover:shadow-md"}`}
                            data-testid={`notebook-item-${it.id}`}>
                        {canEdit && (
                          <button type="button" {...handle} title="Arrastrar para acomodar"
                                  className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 shrink-0 -ml-1 touch-none"
                                  data-testid={`notebook-drag-${it.id}`}>
                            <GripVertical className="h-4 w-4" />
                          </button>
                        )}
                        <div className="rounded-lg bg-slate-100 p-2 shrink-0"><Icon className="h-4 w-4 text-slate-600" /></div>
                        <div className="min-w-0 flex-1">
                          <button
                            type="button"
                            onClick={() => openItem(it)}
                            className="text-sm font-semibold text-slate-900 hover:text-brand truncate block text-left w-full"
                            data-testid={`notebook-open-${it.id}`}
                          >
                            {it.name}
                          </button>
                          <div className="text-[11px] text-slate-500 truncate">
                            {it.created_by_name || ""}{it.created_at ? ` · ${crDate(it.created_at)}` : ""}
                          </div>
                          <div className="mt-2 flex items-center gap-1">
                            <Button size="sm" variant="outline" onClick={() => openItem(it)} data-testid={`notebook-cta-${it.id}`}>
                              <ExternalLink className="h-3.5 w-3.5" /> Abrir
                            </Button>
                            {canEdit && (
                              <>
                                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setRenaming({ id: it.id, name: it.name })} data-testid={`notebook-rename-${it.id}`}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(it)} data-testid={`notebook-delete-${it.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </Card>
                    )}
                  </Sortable>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-sm" data-testid="notebook-new-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{newForm.kind === "sheet" ? "Nueva hoja de cálculo" : "Nuevo documento"}</DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            placeholder="Ej: Clase 12 · verbos irregulares"
            value={newForm.title}
            onChange={(e) => setNewForm({ ...newForm, title: e.target.value })}
            data-testid="notebook-new-title"
          />
          {templates.filter((tp) => tp.kind === newForm.kind).length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Formato de origen</p>
              <Select
                value={newForm.template_id || "blank"}
                onValueChange={(v) => setNewForm({ ...newForm, template_id: v === "blank" ? "" : v })}
              >
                <SelectTrigger data-testid="notebook-template-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="blank">En blanco</SelectItem>
                  {templates.filter((tp) => tp.kind === newForm.kind).map((tp) => (
                    <SelectItem key={tp.id} value={tp.id}>{tp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <p className="text-[11px] text-slate-500">Se crea en Google y se abre en otra pestaña. Cualquiera con el enlace del notebook puede editarlo.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Cancelar</Button>
            <Button onClick={createDoc} disabled={busy} className="min-w-0" data-testid="notebook-new-save">
              <span className="truncate">{busy ? "Creando…" : "Crear y abrir"}</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
        <DialogContent className="max-w-sm" data-testid="notebook-rename-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Renombrar</DialogTitle></DialogHeader>
          <Input value={renaming?.name || ""} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })} data-testid="notebook-rename-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenaming(null)}>Cancelar</Button>
            <Button onClick={rename} data-testid="notebook-rename-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-6xl h-[88vh] flex flex-col" data-testid="notebook-viewer" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-3">
              <span className="truncate">{viewing?.name}</span>
              <Button size="sm" variant="outline" asChild data-testid="notebook-viewer-edit">
                <a href={viewing?.web_link} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-3.5 w-3.5" /> Editar en Google
                </a>
              </Button>
            </DialogTitle>
          </DialogHeader>
          <iframe
            title={viewing?.name || "documento"}
            src={viewing ? previewSrc(viewing) : undefined}
            className="flex-1 w-full rounded-lg border border-slate-200 bg-white"
          />
          <p className="text-[11px] text-slate-500">
            Vista dentro de la app: se lee pero no se edita (Google no permite editar sus documentos dentro de otra app).
            Para escribir, usá "Editar en Google".
          </p>
        </DialogContent>
      </Dialog>

      <ResourcePicker open={pickerOpen} onOpenChange={setPickerOpen} selected={[]} onConfirm={addFromResources} />
    </div>
  );
};

export default NotebookPanel;
